import axios from "axios";
import { APIOverrides } from "../../ApiOverrides"
import type { BracketCompetitionProps, CompetitionPayoutTypeProps, CompetitionPlayerProps, CompetitionProps, CompetitionRecordProps, CompetitionResultProps, CompetitionResultTypeProps, CompetitionSeasonProps, CompetitionTypeProps, CreateGroupStepProps, EventProps, GroupMessageProps, GroupPlayerProps, GroupProps, NewOverallAnalytics, OrderAnalyticsProps, OrderProps, PollCampaignProps, PollProps, PostProps, PublicPlayerProps, SquaresCompetitionProps } from "../../types";
import type { Moment } from "moment-mini";
import moment from "moment-mini";

let EVENT_SVC_API = ''
let AUTH_SVC_API = ''
let MK_SVC_API = ''
let SOCIAL_SVC_API = ''
let TP_SVC_API = ''

export { GroupApi, GroupHelpers }

const GroupApi = {
    setEnvironment: () => {
        const endpoints = APIOverrides.getEndpoints();
        EVENT_SVC_API = endpoints['EVENT_SVC_API'] as string;
        MK_SVC_API = endpoints['MK_SVC_API'] as string;
        TP_SVC_API = endpoints['TP_SVC_API'] as string;
        AUTH_SVC_API = endpoints['AUTH_SVC_API'] as string;
        SOCIAL_SVC_API = endpoints['SOCIAL_SVC_API'] as string;
    },
    createGroup: async(name?:string, description?:string, invite_code?:string):Promise<GroupProps | undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/group/create`, { name, description, invite_code });
            return resp.data.group
        } catch (e) {
            console.log(e)
            return undefined
        }
    },
    activateGroup: async(group_id:string):Promise<GroupProps | undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/group/activate`, { group_id });
            return resp.data.group
        } catch (e) {
            console.log(e)
            return undefined
        }
    },
    deleteGroup: async(group_id:string):Promise<GroupProps|undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/group/delete`, { group_id });
            return resp.data.group
        } catch (e) {
            console.log(e)
            return undefined
        }
    },
    getGroupById: async(group_id:string):Promise<undefined|{ group:GroupProps, group_messages:GroupMessageProps[], group_players:GroupPlayerProps[], my_group_player?:GroupPlayerProps }> => {
        try {
            const resp = await axios.get(`${SOCIAL_SVC_API}/v2/groups/group/${group_id}`);
            return resp.data
        } catch (e) {
            console.log(e);
            return undefined
        }
    },
    updateGroupPlayer: async(group_player:GroupPlayerProps):Promise<GroupPlayerProps | undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/player/update`, { group_player });
            return resp.data.group_player
        } catch (e) {
            return undefined
        }
    },
    acceptInvite:async(group_player_id:string):Promise<GroupPlayerProps | undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/player/accept`, { group_player_id });
            return resp.data.group_player
        } catch (e) {
            return undefined
        }
    },
    uninvitePlayer: async(group_player_id:string):Promise<GroupPlayerProps|undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/player/uninvite`, { group_player_id });
            return resp.data.group_player
        } catch (e) {
            return undefined
        }
    },
    bootPlayer: async(group_player_id:string):Promise<GroupPlayerProps|undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/player/boot`, { group_player_id });
            return resp.data.group_player
        } catch (e) {
            return undefined
        }
    },
    declineInvite:async(group_player_id:string):Promise<GroupPlayerProps | undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/player/decline`, { group_player_id });
            return resp.data.group_player
        } catch (e) {
            return undefined
        }
    },
    join:async(group_id:string, invite_code?:string):Promise<GroupPlayerProps | undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/player/join`, { group_id, invite_code });
            return resp.data.group_player
        } catch (e) {
            return undefined
        }
    },
    leave:async(group_player_id:string):Promise<GroupPlayerProps | undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/player/leave`, { group_player_id });
            return resp.data.group_player
        } catch (e) {
            return undefined
        }
    },
    updateGroup: async(group:GroupProps):Promise<GroupProps|undefined> => {
        try {
            const resp = await axios.post(`${SOCIAL_SVC_API}/v2/groups/group/update`, {group});
            return resp.data.group
        } catch (e) {
            console.log(e);
            return undefined
        }
    },
    getPlayersByIds: async(player_ids:string[]):Promise<PublicPlayerProps[]> => {
        try {
            if(player_ids.length == 0){ return [] }
            const resp = await axios.post(`${AUTH_SVC_API}/v1/players/bulk/get`, { player_ids });
            return resp.data.players
        } catch (e) {
            return []
        }
    },
    getGroupAnalytics: async(group_id:string, to:number, from:number):Promise<OrderAnalyticsProps[]> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/analytics/group/details/${group_id}?to=${to}&from=${from}`);
            return resp.data.analytics
        } catch (e) {
            return []
        }
    },
    getCompetitionOptions: async():Promise<{competition_types:CompetitionTypeProps[], competition_result_types:CompetitionResultTypeProps[], competition_payout_types:CompetitionPayoutTypeProps[]}> => {
        const resp = await axios.get(`${TP_SVC_API}/v1/competitions/options`)
        return resp.data
    },
    getActiveCompetitions: async(group_id:string):Promise<{ competitions:CompetitionProps[], competition_players:CompetitionPlayerProps[], competition_records:CompetitionRecordProps[], competition_results:CompetitionResultProps[] }> => {
        try {
            const resp = await axios.get(`${TP_SVC_API}/v2/competitions/group/active/${group_id}`);
            return resp.data
        } catch (e) {
            console.log(e);
            return { competition_players:[], competition_records:[], competition_results:[], competitions:[] }
        }
    },
    getHistoryCompetitions: async(group_id:string, offset:number):Promise<{ competitions:CompetitionProps[], competition_players:CompetitionPlayerProps[], competition_records:CompetitionRecordProps[], competition_results:CompetitionResultProps[] }> => {
        try {
            const resp = await axios.get(`${TP_SVC_API}/v2/competitions/group/history/${group_id}?offset=${offset}`);
            return resp.data
        } catch (e) {
            console.log(e);
            return { competition_players:[], competition_records:[], competition_results:[], competitions:[] }
        }
    },
    getActivePolls: async(group_id:string):Promise<{poll_campaigns:PollCampaignProps[], polls:PollProps[]}> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/polls/group/active/${group_id}`);
            return resp.data
        } catch (e) {
            return { poll_campaigns: [], polls:[] }
        }
    },
    getHistoryPolls: async(group_id:string, offset:number):Promise<PollCampaignProps[]> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/polls/group/history/${group_id}?offset=${offset}`);
            return resp.data.poll_campaigns
        } catch (e) {
            return []
        }
    },
    getActiveSquares: async(group_id:string):Promise<SquaresCompetitionProps[]> => {
        try {
            const resp = await axios.get(`${TP_SVC_API}/v1/squares/group/active/${group_id}`);
            return resp.data.squares_competitions
        } catch (e) {
            return []
        }
    },
    getHistorySquares: async(group_id:string, offset:number):Promise<SquaresCompetitionProps[]> => {
        try {
            const resp = await axios.get(`${TP_SVC_API}/v1/squares/group/history/${group_id}?offset=${offset}`);
            return resp.data.squares_competitions
        } catch (e) {
            return []
        }
    },
    getEventsByIds: async(event_ids:string[]):Promise<EventProps[]> => {
        try {
            if(event_ids.length == 0){ return [] }
            const resp = await axios.post(`${EVENT_SVC_API}/v1/events/bulk/get`, { attribute:'event_id', values: event_ids });
            return resp.data.events
        } catch (e) {
            return []
        }
    },
    getPostsByGroupId: async(group_id:string, offset:number):Promise<PostProps[]> => {
        try {
            const resp = await axios.get(`${SOCIAL_SVC_API}/v1/posts/group/${group_id}?offset=${offset}`);
            return resp.data.posts
        } catch (e) {
            console.log(e)
            return []
        }
    },
    getBulkOrders: async(order_ids:string[]):Promise<OrderProps[]> => {
        try {
            const resp = await axios.post(`${MK_SVC_API}/v1/orders/bulk/get`, { order_ids })
            return resp.data.orders
        } catch (e) {
            console.log(e);
            return []
        }
    },
}


const GroupHelpers = {
    sortMembers: (group_players:GroupPlayerProps[], order_analytics:OrderAnalyticsProps[], active_stat?:string) => {
        let player_analytics: { [key:string]: NewOverallAnalytics } = {}
        group_players.map(p => {
            let my_analytics = order_analytics.filter(oa => oa.player_id == p.player_id);
            player_analytics[p.player_id] = GroupHelpers.aggregateAnalytics(my_analytics);
        });

        let sorted_players = group_players.filter(gp => gp.status != 'inactive').sort((a,b) => {
            let a_active = a.status == 'active' ? 1 : 0
            let b_active = b.status == 'active' ? 1 : 0
            let a_analytics = player_analytics[a.player_id]
            let b_analytcs = player_analytics[b.player_id]
            let a_analytic = a_analytics ? a_analytics[active_stat as keyof NewOverallAnalytics] ?? 0 : 0
            let b_analytic = b_analytcs ? b_analytcs[active_stat as keyof NewOverallAnalytics] ?? 0 : 0
            if(!a.leaderboard){ a_analytic = -99999999 }
            if(!b.leaderboard){ b_analytic = -99999999 }
            return b_active - a_active || parseFloat(b_analytic as string) - parseFloat(a_analytic as string)
        });
        return { sorted_players, player_analytics }
    },
    sortCompetitionAction: ( seasons:CompetitionSeasonProps[], competitions:CompetitionProps[], bracket_competitions:BracketCompetitionProps[], squares_competitions:SquaresCompetitionProps[], poll_campaigns:PollCampaignProps[] ):{ id:string, type:string, scheduled_datetime:Moment }[] => {
        let data:{ id: string, type: string, scheduled_datetime:Moment }[] = []
        seasons.map(s => data.push({ id: s.competition_season_id, type: 'season', scheduled_datetime: moment(s.scheduled_datetime) }));
        competitions.map(c => data.push({ id:c.competition_id, type: 'competition', scheduled_datetime: moment(c.scheduled_datetime) }));
        bracket_competitions.map(bc => data.push({ id: bc.bracket_competition_id, type: 'bracket', scheduled_datetime: moment(bc.scheduled_datetime) }));
        squares_competitions.map(sc => data.push({ id: sc.sq_comp_id, type: 'squares', scheduled_datetime: moment(sc.begin_datetime) }));
        poll_campaigns.filter(pc => pc.market_type == 'FREE').map(pc => data.push({ id: pc.poll_campaign_id, type: 'poll_campaign', scheduled_datetime: moment(pc.create_datetime) }));
        return data.sort((a,b) => a.scheduled_datetime.unix() - b.scheduled_datetime.unix())
    },
    generateGroup: ():GroupProps => {
        return {
            group_id: '',
            description: ``,
            name: '',
            competition_alerts: false,
            position_alerts: false,
            order_alerts: false,
            group_admin: '0',
            group_image: {},
            invite_only: true,
            invite_code: (Math.random()*10000000000).toFixed(),
            leaderboard_ind: false,
            type: 'multiple',
            status:'pending',
            create_datetime: '',
            last_update_datetime: ''
        }
    },
    isStepValid: (step:CreateGroupStepProps, draft_group:GroupProps):string[] => {
        let errors:string[] = []
        switch(step.step_key){
            case 'info':
                if(!draft_group.name){ errors.push('Please provide a valid name') }
                if(!draft_group.description){ errors.push('Please provide a description') }
                break
            case 'image':
                if(!draft_group.group_image?.url){ errors.push('Please add an image to continue') }
                break
            default: break
        }
        return errors
    },
    aggregateAnalytics: (analytics:OrderAnalyticsProps[]):NewOverallAnalytics => {
        const wins = analytics.reduce((a,b) => a + b.wins, 0);
        const losses = analytics.reduce((a,b) => a + b.losses, 0);
        const draws = analytics.reduce((a,b) => a + b.draws, 0);
        const fulfilled_positions = analytics.reduce((a,b) => a + parseFloat(b.fulfilled_positions as any), 0);
        const winnings = analytics.reduce((a,b) => a + b.winnings, 0);
        const original_stake = analytics.reduce((a,b) => a + b.original_stake, 0);
        const cash_rcvd = analytics.reduce((a,b) => a + b.cash_rcvd, 0);
        
        const win_pct = fulfilled_positions > 0 ? (wins + (draws / 2)) / fulfilled_positions : 0
        const earnings = (winnings + cash_rcvd) - original_stake
        const roi_pct = original_stake > 0 ? earnings / original_stake : 0
        return {
            winnings,
            original_stake,
            wins,
            losses,
            draws,
            net_winnings: 0,
            player_id: '',
            fulfilled_positions,
            cash_rcvd,
            earnings,
            win_pct,
            roi_pct
        }
    }
}